import { useEffect, useMemo, useState } from "react";

export default function Filters({
  categories = [],
  activeCategories = [],
  onApplyFilters,
  isNew = false,
  onToggleNew,
  filterTypes = [
    { type: "caption", label: "Content Pillar" },
    { type: "doneforyou", label: "Niche" },
    { type: "destination", label: "Destination" },
  ],
}) {
  const safeCategories = Array.isArray(categories) ? categories : [];
  const safeActiveCategories = Array.isArray(activeCategories)
    ? activeCategories
    : [];
  const safeFilterTypes = Array.isArray(filterTypes) ? filterTypes : [];

  const [selectedCategories, setSelectedCategories] = useState(
    safeActiveCategories
  );

  useEffect(() => {
    setSelectedCategories(safeActiveCategories);
  }, [safeActiveCategories]);

  const toggleCategory = (idcategory) => {
    const clickedCategory = safeCategories.find(
      (cat) => cat.idcategory === idcategory
    );

    if (!clickedCategory) return;

    setSelectedCategories((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const alreadySelected = current.includes(idcategory);

      if (alreadySelected) {
        return current.filter((id) => id !== idcategory);
      }

      const withoutSameType = current.filter((id) => {
        const cat = safeCategories.find((c) => c.idcategory === id);
        return cat?.type !== clickedCategory.type;
      });

      return [...withoutSameType, idcategory];
    });
  };

  const clearAllFilters = () => {
    setSelectedCategories([]);
  };

  const applyFilters = (e) => {
    e?.currentTarget?.blur();
    onApplyFilters?.(selectedCategories);
  };

  const removeAppliedCategory = (idcategory) => {
    onApplyFilters?.(safeActiveCategories.filter((id) => id !== idcategory));
  };

  const removeSelectedCategory = (idcategory) => {
    setSelectedCategories((prev) =>
      (Array.isArray(prev) ? prev : []).filter((id) => id !== idcategory)
    );
  };

  const appliedFilterObjects = useMemo(() => {
    return safeCategories.filter((cat) =>
      safeActiveCategories.includes(cat.idcategory)
    );
  }, [safeCategories, safeActiveCategories]);

  const selectedFilterObjects = useMemo(() => {
    return safeCategories.filter((cat) =>
      selectedCategories.includes(cat.idcategory)
    );
  }, [safeCategories, selectedCategories]);

  const renderFilterGroup = (type, label) => {
    const filtered = safeCategories.filter((c) => c.type === type);

    if (!filtered.length) return null;

    return (
      <div className="border rounded-3 p-2 mb-3" key={type}>
        <label className="text-dark fw-bold mb-2">{label}</label>

        <div className="d-flex flex-wrap gap-2">
          {filtered.map((cat) => {
            const isActive = selectedCategories.includes(cat.idcategory);

            return (
              <button
                key={cat.idcategory}
                type="button"
                className={`btn filter-btn ${
                  isActive ? "btn-primary" : "btn-light"
                }`}
                onClick={() => toggleCategory(cat.idcategory)}
              >
                {cat.title}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="my-4">
        <div className="row align-items-end justify-content-md-end">
          <div className="col-md-9 text-start">
            <div className="selected-filters-wrap">
              <label
                className={`text-muted applied-filters-label mb-2 ${
                  appliedFilterObjects.length > 0 ? "" : "d-none"
                }`}
              >
                Applied filters
              </label>

              <div className="d-flex flex-wrap gap-2 applied-filters">
                {appliedFilterObjects.map((cat) => (
                  <button
                    key={cat.idcategory}
                    type="button"
                    className="btn btn-sm btn-light"
                    onClick={() => removeAppliedCategory(cat.idcategory)}
                  >
                    {cat.title} <i className="fi fi-rr-cross ms-2"></i>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="col-md-3 text-end">
            <button
              type="button"
              className={`btn btn-outline-primary me-2 ${
                isNew ? "active" : ""
              }`}
              onClick={onToggleNew}
            >
              New
            </button>

            <button
              type="button"
              className="btn btn-outline-primary"
              data-bs-toggle="modal"
              data-bs-target="#filterModal"
            >
              <i className="fi fi-rr-settings-sliders"></i>
            </button>
          </div>
        </div>
      </div>

      <div className="modal fade" id="filterModal" tabIndex="-1">
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
          <div className="modal-content rounded-4">
            <div className="modal-header pb-0 border-0">
              <h5 className="modal-title fw-bold text-dark">Filtering</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              ></button>
            </div>

            <div className="modal-body">
              {safeFilterTypes.map((filter) =>
                renderFilterGroup(filter.type, filter.label)
              )}
            </div>

            <div className="modal-footer border-0 shadow-lg">
              <div className="d-flex justify-content-between align-items-center w-100 flex-wrap gap-3">
                <div className="selected-filters-wrap">
                  <label
                    className={`text-muted selected-filters-label mb-2 ${
                      selectedFilterObjects.length > 0 ? "" : "d-none"
                    }`}
                  >
                    Filters to be applied
                  </label>

                  <div className="d-flex flex-wrap gap-2 selected-filters">
                    {selectedFilterObjects.map((cat) => (
                      <button
                        key={cat.idcategory}
                        type="button"
                        className="btn btn-sm btn-light"
                        onClick={() => removeSelectedCategory(cat.idcategory)}
                      >
                        {cat.title} <i className="fi fi-rr-cross ms-2"></i>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    id="clearFilters"
                    onClick={clearAllFilters}
                  >
                    Clear All Filters
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary px-4"
                    id="applyFilters"
                    onClick={applyFilters}
                    data-bs-dismiss="modal"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}